import type { ExportFormat, RenderSettings } from '@/types/engine';
import type { AppState } from '@/state/appState';
import type { EngineClient } from '@/workers/EngineClient';
import type { StringArtRenderer } from '@/renderer/StringArtRenderer';
import { buildProject, serializeProject } from './project';
import { toClampedRgba } from '@/image/decode';
import { exportFileName } from './download';

export interface ExportResult {
  blob: Blob;
  filename: string;
}

export interface ExportContext {
  state: AppState;
  client: EngineClient;
  renderer: StringArtRenderer | null;
  wasmVersion: string;
  pngSize: number;
  /** Use the software renderer (worker) instead of the canvas for PNG. */
  pngSoftware?: boolean;
}

const MIME: Record<Exclude<ExportFormat, 'png'>, string> = {
  svg: 'image/svg+xml',
  csv: 'text/csv',
  json: 'application/json',
  dxf: 'application/dxf',
  gcode: 'text/x-gcode',
};

export const EXPORT_FORMATS: Array<{ id: ExportFormat; label: string; description: string }> = [
  { id: 'png', label: 'PNG', description: 'Rendered image' },
  { id: 'svg', label: 'SVG', description: 'One <line> per string' },
  { id: 'json', label: 'JSON project', description: 'Reopen later, machine readable' },
  { id: 'csv', label: 'CSV', description: 'sequence,start_pin,end_pin' },
  { id: 'dxf', label: 'DXF', description: 'CAD / laser templates (mm)' },
  { id: 'gcode', label: 'G-code', description: 'Plotter path through the pins' },
];

export async function exportAs(format: ExportFormat, ctx: ExportContext): Promise<ExportResult> {
  const { state } = ctx;
  if (state.solution.pins.length === 0) throw new Error('Nothing to export yet: generate pins and strings first.');
  const strings = state.solution.count;
  const name = state.source.name || 'string-art';
  if (format === 'png') {
    const size = Math.max(64, Math.min(8192, Math.round(ctx.pngSize)));
    let blob: Blob;
    if (ctx.pngSoftware || !ctx.renderer) {
      const aspect = state.target.height / state.target.width;
      const w = aspect <= 1 ? size : Math.round(size / aspect);
      const h = aspect <= 1 ? Math.round(size * aspect) : size;
      const rgba = await ctx.client.render(w, h, state.render);
      blob = await rgbaToPngBlob(rgba, w, h);
    } else {
      const { render, ui } = state;
      blob = await ctx.renderer.exportPng(
        size,
        {
          color: render.stringColor,
          opacity: render.stringOpacity,
          thickness: render.stringThickness,
          background: render.backgroundColor,
          blendMode: ui.blendMode,
          roughness: ui.stringRoughness,
          brightness: ui.stringBrightness,
        },
        state.playback.index,
        render.showPins,
        render.pinColor,
        render.pinRadius,
      );
    }
    return { blob, filename: exportFileName(name, strings, 'png') };
  }
  if (format === 'json') {
    const text = serializeProject(buildProject(state, ctx.wasmVersion));
    return { blob: new Blob([text], { type: MIME.json }), filename: exportFileName(name, strings, 'json') };
  }
  const render: RenderSettings = state.render;
  const text = await ctx.client.export(format, render, state.exportOptions);
  return { blob: new Blob([text], { type: MIME[format] }), filename: exportFileName(name, strings, format) };
}

export async function rgbaToPngBlob(rgba: Uint8Array, width: number, height: number): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D is not available.');
  ctx.putImageData(new ImageData(toClampedRgba(rgba), width, height), 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png');
  });
}
